/**
 * Broker Integration Mapper Service
 * Transforms raw Firestore data to view models
 */

import {
  resolveBalanceData,
  buildAccountDataFromMetrics
} from '../utils/brokerBalance';
import { extractAccountId } from '../utils/accountDataStandardizer';

/**
 * Parse expiration date from various formats
 * @param {any} value - Date value
 * @returns {Date|null} Parsed date or null
 */
const parseExpirationDate = (value) => {
  if (!value) return null;

  try {
    if (typeof value === 'string') {
      const str = value.trim();
      // Handle DD/MM/YYYY explicitly (e.g., 02/09/2025 => 2 Sept 2025)
      const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const match = str.match(ddmmyyyy);
      if (match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1;
        const year = parseInt(match[3], 10);
        return new Date(year, month, day);
      }
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

/**
 * Determine real-time connection status
 * @param {object} integration - Raw integration data
 * @returns {string} Status: 'connected', 'expired', 'needs_reauth', or 'disconnected'
 */
const determineConnectionStatus = (integration) => {
  // Issue #44: pass `needs_reauth` through unchanged so UI can surface a
  // "Reconnect Required" CTA. This is distinct from "disconnected" (never connected)
  // and "expired" (token expired but integration otherwise healthy).
  if (integration.status === 'needs_reauth') {
    return 'needs_reauth';
  }

  if (integration.status !== 'active' && integration.status !== 'connected') {
    return 'disconnected';
  }

  const hasToken = integration.credentials?.accessToken ||
    integration.credentials?.access_token;

  if (!hasToken) {
    return 'disconnected';
  }

  // Try standardized field first, then legacy fields (including expires_at from backend)
  const expiryDate = parseExpirationDate(integration.credentials?.expiresAt) ||
    parseExpirationDate(integration.credentials?.expires_at) ||
    parseExpirationDate(integration.metadata?.tokenExpirationDate) ||
    parseExpirationDate(integration.metadata?.expirationDate) ||
    parseExpirationDate(integration.credentials?.sessionExpiration);

  console.log('🔍 Connection status check:', {
    broker: integration.broker,
    hasToken,
    expiryFields: {
      'credentials.expiresAt': integration.credentials?.expiresAt,
      'metadata.tokenExpirationDate': integration.metadata?.tokenExpirationDate,
      'metadata.expirationDate': integration.metadata?.expirationDate,
      'credentials.sessionExpiration': integration.credentials?.sessionExpiration
    },
    parsedExpiryDate: expiryDate,
    now: new Date(),
    isExpired: expiryDate ? expiryDate <= new Date() : 'unknown'
  });

  if (!expiryDate) {
    // If we can't determine expiry but have a token and active status, assume connected
    // This is less conservative but more practical for working integrations
    if (hasToken && (integration.status === 'active' || integration.status === 'connected')) {
      console.log('⚠️ No expiry date found but has valid token and active status, assuming connected');
      return 'connected';
    }
    console.log('⚠️ No expiry date found and no valid token, marking as disconnected');
    return 'disconnected';
  }

  const now = new Date();
  const isExpired = expiryDate <= now;
  const status = isExpired ? 'expired' : 'connected';

  console.log(`🔍 Final status for ${integration.broker}: ${status} (expires: ${expiryDate.toISOString()})`);
  return status;
};

/**
 * Extract selected account information
 * @param {object} integration - Raw integration data
 * @returns {object|null} Selected account info
 */
const extractSelectedAccount = (integration) => {
  if (integration.status !== 'active' && integration.status !== 'connected') {
    return null;
  }

  // Check credentials.accountId first (single source of truth) using standardized extraction
  const selectedAccount = extractAccountId(integration.credentials) ||
    extractAccountId(integration.metadata?.selectedAccount) ||
    integration.accountslecting || // Legacy typo fields
    integration.accountselecting ||
    integration.accountSelecting;

  if (selectedAccount) {
    const accountNumber = extractAccountId(selectedAccount) || selectedAccount;

    if (accountNumber) {
      const balanceInfo = resolveBalanceData(integration, accountNumber);
      const accountData = buildAccountDataFromMetrics(balanceInfo);

      return {
        id: integration.id,
        broker: integration.broker,
        accountNumber: String(accountNumber),
        accountData,
        accountType: integration.metadata?.accountType || 'live',
        accountName: integration.metadata?.accountName,
        lastRefresh: integration.updated_at || integration.metadata?.lastAccountSync
      };
    }
  }

  // Fallback: Check for account info in various structures
  let primaryAccount = integration.accountInfo ||
    (integration.accounts && integration.accounts[0]) || {};

  if (!primaryAccount && integration.account) {
    if (Array.isArray(integration.account) && integration.account.length > 0) {
      primaryAccount = integration.account[0];
    } else if (typeof integration.account === 'object' && integration.account['0']) {
      primaryAccount = integration.account['0'];
    }
  }

  // Extract account number using standardized extraction
  const accountNumber = extractAccountId(primaryAccount) ||
    (Array.isArray(primaryAccount?.account) && primaryAccount.account.length > 0
      ? extractAccountId(primaryAccount.account[0])
      : null) ||
    integration.metadata?.balanceData?.primaryAccountNumber ||
    integration.metadata?.primaryAccountNumber;

  if (accountNumber) {
    const balanceInfo = resolveBalanceData(integration, accountNumber);
    const accountData = buildAccountDataFromMetrics(balanceInfo);

    return {
      id: integration.id,
      broker: integration.broker,
      accountNumber: String(accountNumber),
      accountData,
      accountType: primaryAccount['margin-or-cash'] ||
        primaryAccount.accountType ||
        primaryAccount.type || 'live',
      accountName: primaryAccount['nickname'] ||
        primaryAccount.accountName ||
        primaryAccount.name,
      lastRefresh: integration.updated_at || integration.metadata?.lastAccountSync
    };
  }

  return null;
};

/**
 * Map single broker integration
 * @param {object} rawIntegration - Raw Firestore data
 * @returns {object} Mapped integration
 */
const mapSingleIntegration = (rawIntegration) => {
  const status = determineConnectionStatus(rawIntegration);

  return {
    id: rawIntegration.id,
    broker: rawIntegration.broker,
    status,
    credentials: {
      accountId: rawIntegration.credentials?.accountId,
      selectedAccountId: rawIntegration.credentials?.selectedAccountId || rawIntegration.credentials?.accountId,
      userUsername: rawIntegration.credentials?.userUsername,
      accessToken: rawIntegration.credentials?.accessToken,
      access_token: rawIntegration.credentials?.access_token,
      expiresAt: rawIntegration.credentials?.expiresAt,
      expires_at: rawIntegration.credentials?.expires_at,
      sessionExpiration: rawIntegration.credentials?.sessionExpiration,
      updatedAt: rawIntegration.credentials?.updatedAt
    },
    metadata: {
      accountType: rawIntegration.metadata?.accountType || 'live',
      accountName: rawIntegration.metadata?.accountName,
      brokerDisplayName: rawIntegration.metadata?.brokerDisplayName ||
        rawIntegration.broker.charAt(0).toUpperCase() +
        rawIntegration.broker.slice(1).replace('_', ' '),
      connectedUsername: rawIntegration.metadata?.connectedUsername,
      lastAccountSync: rawIntegration.metadata?.lastAccountSync,
      selectedAccount: rawIntegration.metadata?.selectedAccount,
      balanceData: rawIntegration.metadata?.balanceData,
      lastBalanceRefresh: rawIntegration.metadata?.lastBalanceRefresh,
      tokenExpirationDate: rawIntegration.metadata?.tokenExpirationDate,
      expirationDate: rawIntegration.metadata?.expirationDate,
      // Issue #44: ExpiredTokenCard renders this as text when
      // status === 'needs_reauth'. Only the string reason is passed through —
      // `needsReauthAt` is intentionally omitted because Firestore Timestamps
      // are objects and React would crash if any component rendered it
      // directly. Re-add with ISO-string normalization if a future consumer
      // needs the timestamp.
      needsReauthReason: rawIntegration.metadata?.needsReauthReason
    },
    connectionDate: rawIntegration.connectionDate,
    lastSync: rawIntegration.lastSync,
    updatedAt: rawIntegration.updatedAt
  };
};

/**
 * Map broker integrations from Firestore snapshot
 * @param {array} rawIntegrations - Array of raw Firestore documents
 * @returns {object} Mapped connections and selected accounts
 */
export const mapBrokerIntegrations = (rawIntegrations) => {
  if (!rawIntegrations || !Array.isArray(rawIntegrations)) {
    return {
      connections: [],
      selectedAccounts: []
    };
  }

  const connections = [];
  const selectedAccounts = [];
  const processedKeys = new Set();

  rawIntegrations.forEach((rawIntegration) => {
    // Map integration
    const mappedIntegration = mapSingleIntegration(rawIntegration);

    // Deduplicate by broker + account using standardized extraction
    const accountId = extractAccountId(mappedIntegration.credentials) ||
      extractAccountId(mappedIntegration.metadata) ||
      mappedIntegration.metadata?.connectedUsername ||
      mappedIntegration.id;
    const key = `${mappedIntegration.broker}_${accountId}`;

    if (!processedKeys.has(key)) {
      processedKeys.add(key);
      connections.push(mappedIntegration);

      // Extract selected account if active
      if (mappedIntegration.status === 'connected') {
        const selectedAccount = extractSelectedAccount(rawIntegration);
        if (selectedAccount) {
          selectedAccounts.push(selectedAccount);
        }
      }
    }
  });

  return {
    connections,
    selectedAccounts
  };
};

/**
 * Normalize broker integration (backward compatibility)
 * @param {object} integration - Raw or mapped integration
 * @returns {object} Normalized integration
 */
export const normalizeBrokerIntegration = (integration) => {
  if (!integration) return null;

  // If already mapped, return as-is
  if (integration._normalized) {
    return integration;
  }

  // Otherwise, map it
  const mapped = mapSingleIntegration(integration);
  mapped._normalized = true;
  return mapped;
};